import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import {
  ToQueryStringFilters,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import type { TInventory } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination } from '@/core/types'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '@/core/store/catalogs'
import { useDetailsQuery } from '../query/useDetailsQuery'

const [useProvideModule, useModule] = createInjectionState(() => {
  const form = ref<TInventory>({})
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)
  const modelId = ref<number>()

  const materialsFilters = ref<ToQueryStringFilters>({
    param: '',
    status_id: [1],
    order_by: 'created_at,asc',
  })

  const filters = ref<ToQueryStringFilters>({
    param: '',
    search: '',
    id: [],
    status_id: [],
    warehouse_id: [],
    part_number: '',
    dates: {
      created_at: [],
    },
    galvanized_grade_id: [],
    gauge_id: [],
    hardness_id: [],
    order_by: 'created_at,asc',
  })

  const millFilters = ref<ToQueryStringFilters>({
    status_id: [1],
    order_by: 'created_at,desc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['id', 'part_number', 'measurementUnit.name'],
  })

  const materialsQueryParams = useToQueryString(toReactive(materialsFilters), {
    searchColumns: ['id', 'part_number', 'name'],
  })
  const millQueryParams = useToQueryString(toReactive(millFilters))

  const paginationModule = usePagination()
  const paginationDetails = usePagination()

  const moduleQuery = useModuleQuery({
    modelId: computed(() => Number(modelId.value)),
    currentPage: paginationModule.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
      loadSummary: moduleQueryParams.queryParams,
      articlesCatalog: materialsQueryParams.queryParams,
      millsCatalog: millQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      loadSummary: true,
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const moduleDetailQuery = useDetailsQuery({
    modelId: computed(() => Number(modelId.value)),
    currentPage: paginationDetails.currentPage,
    enableQuery: {
      loadDetails: computed(
        () => !!modelId.value && !isNaN(Number(modelId.value)),
      ),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationDetails.setPagination(pagination)
      },
    },
  })

  const onRequestCatalog = async (payload: number[]) => {
    await moduleQuery.requestCatalog.mutateAsync({ ids: payload })
  }

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at'],
  })

  const onLoadDetails = (id: number) => {
    modelId.value = id
    paginationDetails.currentPage.value = 1
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const warehouse = computed(() => moduleQuery.warehouseCatalog?.data?.value)
  const articles = computed(() => moduleQuery.articlesCatalog.data?.value)
  const details = computed(() => moduleDetailQuery.loadDetails.data?.value)
  const summary = computed(() => moduleQuery.loadSummary.data?.value)
  const mills = computed(() => moduleQuery?.millsCatalog.data?.value)

  const isArticlesLoading = computed(
    () => moduleQuery.articlesCatalog.isLoading.value,
  )
  const isLoading = computed(() => moduleQuery.loadRecords.isLoading.value)

  const isLoadingDetails = computed(
    () => moduleDetailQuery.loadDetails.isLoading.value,
  )

  const isLoadingCategories = computed(
    () => moduleQuery.requestCatalog.isLoading.value,
  )

  const isLoadingMills = computed(
    () => moduleQuery.millsCatalog.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    form,
    isLoading,
    warehouse,
    materialsFilters,
    articles,
    isArticlesLoading,
    modulePagination: toReactive(paginationModule),
    paginationDetails: toReactive(paginationDetails),
    isLoadingCategories,
    catalogs,
    details,
    isLoadingDetails,
    summary,
    mills,
    isLoadingMills,
    onLoadDetails,
    onRequestCatalog,
  }
})

export { useProvideModule, useModule }
